// ============================================================================
// EDIT PROJECT AVATAR MODAL
// Allows editing all fields of a project-scoped avatar definition in-place.
// ============================================================================

import React, { useState } from 'react';
import { X, Loader2, Bot, ChevronDown, ChevronUp, Sparkles, EyeOff, RefreshCw } from 'lucide-react';
import { updateProjectAvatar, generateAvatarSystemPrompt, generateAvatarImage } from '../../services/avatarCatalogService';
import { AvatarDefinition, AvatarCategory, TTSProvider } from '../../types';

const ELEVENLABS_VOICES = [
  { id: '21m00Tcm4TlvDq8ikWAM', label: 'Rachel (Calm, Female)' },
  { id: 'AZnzlk1XvdvUeBnXmlld', label: 'Domi (Strong, Female)' },
  { id: 'EXAVITQu4vr4xnSDxMaL', label: 'Bella (Soft, Female)' },
  { id: 'ErXwobaYiN019PkySvjV', label: 'Antoni (Well-rounded, Male)' },
  { id: 'MF3mGyEYCl7XYWbV9V6O', label: 'Elli (Emotional, Female)' },
  { id: 'TxGEqnHWrfWFTfGW9XjX', label: 'Josh (Deep, Male)' },
  { id: 'VR6AewLTigWG4xSOukaG', label: 'Arnold (Crisp, Male)' },
  { id: 'pNInz6obpgDQGcFmaJgB', label: 'Adam (Deep, Male)' },
  { id: 'yoZ06aMxZJJ28mfd3POQ', label: 'Sam (Raspy, Male)' },
  { id: 'rOnL0oCXRCiCFsoSTxSB', label: 'Patrick (Deep, Male)' },
  { id: 'PPzYpIqttlTYA83688JI', label: 'Lily (Warm, Female)' },
  { id: 'hmMWXCj9K7N5mCPcRkfC', label: 'Grace (Authoritative, Female)' },
  { id: 'jsCqWAovK2LkecY7zXl4', label: 'River (Calm, Neutral)' },
  { id: 'h8eW5xfRUGVJrZhAFxqK', label: 'Drew (Well-rounded, Male)' },
  { id: 'exsUS4vynmxd379XN4yO', label: 'Matilda (Warm, Female)' },
];

const COLOR_PALETTE = [
  { primary: '#009688', secondary: '#E0F2F1', label: 'Teal' },
  { primary: '#4CAF50', secondary: '#E8F5E9', label: 'Green' },
  { primary: '#FF9800', secondary: '#FFF3E0', label: 'Amber' },
  { primary: '#3F51B5', secondary: '#E8EAF6', label: 'Indigo' },
  { primary: '#E91E63', secondary: '#FCE4EC', label: 'Pink' },
  { primary: '#00BCD4', secondary: '#E0F7FA', label: 'Cyan' },
  { primary: '#673AB7', secondary: '#EDE7F6', label: 'Deep Purple' },
  { primary: '#8BC34A', secondary: '#F1F8E9', label: 'Light Green' },
  { primary: '#FF5722', secondary: '#FBE9E7', label: 'Deep Orange' },
  { primary: '#03A9F4', secondary: '#E1F5FE', label: 'Light Blue' },
  { primary: '#2196F3', secondary: '#E3F2FD', label: 'Blue' },
  { primary: '#9C27B0', secondary: '#F3E5F5', label: 'Purple' },
  { primary: '#F44336', secondary: '#FFEBEE', label: 'Red' },
  { primary: '#607D8B', secondary: '#ECEFF1', label: 'Blue Grey' },
];

interface EditProjectAvatarModalProps {
  projectId: string;
  avatar: AvatarDefinition;
  onClose: () => void;
  onSaved: (updated: AvatarDefinition) => void;
}

export const EditProjectAvatarModal: React.FC<EditProjectAvatarModalProps> = ({
  projectId,
  avatar,
  onClose,
  onSaved
}) => {
  const [name, setName] = useState(avatar.name);
  const [description, setDescription] = useState(avatar.description);
  const [systemPrompt, setSystemPrompt] = useState(avatar.systemPrompt || '');
  const [keywords, setKeywords] = useState((avatar.keywords || []).join(', '));
  const [category, setCategory] = useState(avatar.category || AvatarCategory.CUSTOM);
  const savedVoiceId = avatar.ttsVoiceId || '21m00Tcm4TlvDq8ikWAM';
  const savedIsPredefined = ELEVENLABS_VOICES.some(v => v.id === savedVoiceId);
  const [voiceDropdownValue, setVoiceDropdownValue] = useState(savedIsPredefined ? savedVoiceId : '__custom__');
  const [customVoiceInput, setCustomVoiceInput] = useState(savedIsPredefined ? '' : savedVoiceId);
  const ttsVoiceId = voiceDropdownValue === '__custom__' ? customVoiceInput : voiceDropdownValue;
  const [primaryColor, setPrimaryColor] = useState(avatar.primaryColor || '#009688');
  const [secondaryColor, setSecondaryColor] = useState(avatar.secondaryColor || '#E0F2F1');
  const [isEnabled, setIsEnabled] = useState(avatar.isEnabled !== false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(avatar.imageUrl || null);
  const [pendingImageS3Key, setPendingImageS3Key] = useState<string | null>(null);
  const [error, setError] = useState('');

  const handleColorPreset = (primary: string, secondary: string) => {
    setPrimaryColor(primary);
    setSecondaryColor(secondary);
  };

  const handleGenerateImage = async () => {
    if (!name.trim() || !description.trim()) {
      setError('Name and description are required to generate an image.');
      return;
    }
    setError('');
    setGeneratingImage(true);
    setPreviewImageUrl(null);
    try {
      const result = await generateAvatarImage(projectId, avatar.avatarDefinitionId, name.trim(), description.trim());
      setPreviewImageUrl(result.imageUrl);
      setPendingImageS3Key(result.imageS3Key);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Image generation failed');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleAcceptImage = () => {
    if (previewImageUrl) {
      setPendingImageUrl(previewImageUrl);
      setPreviewImageUrl(null);
    }
  };

  const handleRegenPrompt = async () => {
    if (!name.trim() || !description.trim()) {
      setError('Name and description are required to regenerate.');
      return;
    }
    setError('');
    setRegenerating(true);
    try {
      const result = await generateAvatarSystemPrompt(projectId, name.trim(), description.trim());
      setSystemPrompt(result.systemPrompt);
      setKeywords(result.keywords.join(', '));
      setShowPrompt(true);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to regenerate prompt');
    } finally {
      setRegenerating(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      const kwArray = keywords.split(',').map(k => k.trim()).filter(Boolean);
      const result = await updateProjectAvatar(projectId, avatar.avatarDefinitionId, {
        name: name.trim(),
        description: description.trim(),
        systemPrompt: systemPrompt.trim(),
        keywords: kwArray.length > 0 ? kwArray : [name.trim().toLowerCase()],
        category,
        ttsProvider: TTSProvider.ELEVENLABS,
        ttsVoiceId,
        primaryColor,
        secondaryColor,
        isEnabled,
        ...(pendingImageS3Key
          ? { imageUrl: pendingImageS3Key }
          : pendingImageUrl !== (avatar.imageUrl || null) ? { imageUrl: pendingImageUrl || undefined } : {})
      });
      onSaved(result.avatar);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const fieldLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {text}
    </label>
  );

  const input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <input
      {...props}
      style={{
        width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px',
        fontSize: '14px', color: '#0f172a', background: 'white', boxSizing: 'border-box',
        ...(props.style || {})
      }}
    />
  );

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}
      onClick={onClose}
    >
      <div
        style={{ background: 'white', borderRadius: '14px', width: '100%', maxWidth: '600px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.22)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: secondaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
              {pendingImageUrl
                ? <img src={pendingImageUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Bot size={20} color={primaryColor} />}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>Edit Avatar</h2>
              <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{avatar.name}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}>
            <X size={20} color="#94a3b8" />
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Avatar Image */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              {fieldLabel('Avatar Image')}
              {!generatingImage && !previewImageUrl && (
                <button
                  onClick={handleGenerateImage}
                  disabled={saving}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: '600', color: '#00c7c4', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}
                >
                  <Sparkles size={12} />
                  Generate with AI
                </button>
              )}
            </div>

            {generatingImage && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px', border: '1px dashed #e2e8f0', borderRadius: '10px', color: '#64748b', fontSize: '13px' }}>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
                Generating sketchbook portrait… this takes 15–30s
              </div>
            )}

            {!generatingImage && previewImageUrl && (
              <div>
                <img
                  src={previewImageUrl}
                  alt="Generated avatar preview"
                  style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'block' }}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button
                    onClick={handleAcceptImage}
                    style={{ flex: 1, padding: '7px', background: '#00c7c4', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                  >
                    Accept
                  </button>
                  <button
                    onClick={handleGenerateImage}
                    disabled={generatingImage}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '7px 14px', background: 'white', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                  >
                    <RefreshCw size={12} /> Regen
                  </button>
                </div>
              </div>
            )}

            {!generatingImage && !previewImageUrl && pendingImageUrl && (
              <div>
                <img
                  src={pendingImageUrl}
                  alt="Avatar image"
                  style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'block' }}
                />
                <button
                  onClick={handleGenerateImage}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '6px', fontSize: '12px', fontWeight: '600', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}
                >
                  <RefreshCw size={11} /> Regenerate image
                </button>
              </div>
            )}

            {!generatingImage && !previewImageUrl && !pendingImageUrl && (
              <button
                onClick={handleGenerateImage}
                style={{ width: '100%', padding: '20px', border: '1.5px dashed #e2e8f0', borderRadius: '10px', background: '#f8fafc', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', color: '#94a3b8' }}
              >
                <Bot size={24} color="#cbd5e1" />
                <span style={{ fontSize: '12px', fontWeight: '500' }}>Click to generate a sketchbook portrait</span>
              </button>
            )}
          </div>

          {/* Name + Description */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              {fieldLabel('Name')}
              {input({ value: name, onChange: e => setName(e.target.value), placeholder: 'e.g. Sarah the Buyer' })}
            </div>
            <div>
              {fieldLabel('Category')}
              <select
                value={category}
                onChange={e => setCategory(e.target.value as AvatarCategory)}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', color: '#0f172a', background: 'white', boxSizing: 'border-box' }}
              >
                {Object.values(AvatarCategory).map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            {fieldLabel('Description / Role')}
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', color: '#0f172a', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>

          {/* System Prompt */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
              {fieldLabel('System Prompt')}
              <button
                onClick={handleRegenPrompt}
                disabled={regenerating || saving}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: '600', color: '#00c7c4', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}
              >
                {regenerating ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={12} />}
                Regenerate with AI
              </button>
            </div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
              <button
                onClick={() => setShowPrompt(p => !p)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151' }}
              >
                <span>System Prompt {showPrompt ? '(editing)' : '(click to expand)'}</span>
                {showPrompt ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showPrompt && (
                <textarea
                  value={systemPrompt}
                  onChange={e => setSystemPrompt(e.target.value)}
                  rows={6}
                  style={{ width: '100%', padding: '10px 12px', border: 'none', borderTop: '1px solid #e2e8f0', fontSize: '13px', color: '#334155', lineHeight: '1.6', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
              )}
            </div>
          </div>

          {/* Keywords */}
          <div>
            {fieldLabel('Keywords (comma-separated)')}
            {input({ value: keywords, onChange: e => setKeywords(e.target.value), placeholder: 'e.g. procurement, buyer, B2B' })}
          </div>

          {/* Color picker */}
          <div>
            {fieldLabel('Card Color')}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
              {COLOR_PALETTE.map(c => (
                <button
                  key={c.primary}
                  title={c.label}
                  onClick={() => handleColorPreset(c.primary, c.secondary)}
                  style={{
                    width: '28px', height: '28px', borderRadius: '50%', background: c.primary, border: primaryColor === c.primary ? '3px solid #0f172a' : '2px solid transparent',
                    cursor: 'pointer', flexShrink: 0, transition: 'transform 0.1s', transform: primaryColor === c.primary ? 'scale(1.2)' : 'scale(1)'
                  }}
                />
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Primary (border/icon)</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} style={{ width: '36px', height: '36px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '2px', cursor: 'pointer' }} />
                  {input({ value: primaryColor, onChange: e => setPrimaryColor(e.target.value), style: { flex: 1, fontFamily: 'monospace', fontSize: '13px' } })}
                </div>
              </div>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Secondary (background)</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="color" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)} style={{ width: '36px', height: '36px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '2px', cursor: 'pointer' }} />
                  {input({ value: secondaryColor, onChange: e => setSecondaryColor(e.target.value), style: { flex: 1, fontFamily: 'monospace', fontSize: '13px' } })}
                </div>
              </div>
            </div>
            {/* Preview */}
            <div style={{ marginTop: '10px', padding: '10px 14px', borderRadius: '10px', background: secondaryColor, border: `2px solid ${primaryColor}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: secondaryColor, border: `2px solid ${primaryColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Bot size={16} color={primaryColor} />
              </div>
              <span style={{ fontSize: '13px', fontWeight: '600', color: primaryColor }}>{name || 'Avatar Name'}</span>
            </div>
          </div>

          {/* Voice */}
          <div>
            {fieldLabel('Voice')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <select
                value={voiceDropdownValue}
                onChange={e => setVoiceDropdownValue(e.target.value)}
                style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', color: '#0f172a', background: 'white' }}
              >
                {ELEVENLABS_VOICES.map(v => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
                <option value="__custom__">Custom voice ID...</option>
              </select>
              {voiceDropdownValue === '__custom__' && (
                input({ value: customVoiceInput, onChange: e => setCustomVoiceInput(e.target.value), placeholder: 'Enter ElevenLabs voice ID' })
              )}
            </div>
          </div>

          {/* Enable/Disable toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: '10px', background: isEnabled ? '#f0fdf4' : '#f8fafc', border: `1px solid ${isEnabled ? '#bbf7d0' : '#e2e8f0'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <EyeOff size={15} color={isEnabled ? '#16a34a' : '#94a3b8'} />
              <div>
                <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: isEnabled ? '#15803d' : '#64748b' }}>
                  {isEnabled ? 'Avatar enabled' : 'Avatar disabled'}
                </p>
                <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>
                  {isEnabled ? 'Included in new sessions for this project' : 'Hidden from new sessions — existing sessions unaffected'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsEnabled(v => !v)}
              style={{
                width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                background: isEnabled ? '#16a34a' : '#cbd5e1', position: 'relative', transition: 'background 0.2s', flexShrink: 0
              }}
              aria-label={isEnabled ? 'Disable avatar' : 'Enable avatar'}
            >
              <span style={{
                position: 'absolute', top: '3px', left: isEnabled ? '23px' : '3px',
                width: '18px', height: '18px', borderRadius: '50%', background: 'white',
                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }} />
            </button>
          </div>

          {error && (
            <p style={{ margin: 0, fontSize: '13px', color: '#ef4444', padding: '8px 12px', background: '#fef2f2', borderRadius: '8px' }}>{error}</p>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px', background: '#f8fafc', flexShrink: 0 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{ padding: '9px 16px', background: 'white', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 18px', background: saving ? '#94a3b8' : '#00c7c4', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer' }}
          >
            {saving && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProjectAvatarModal;
